import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FilePreviewDialog from './FilePreviewDialog';
import { buildSafeDownloadFilename } from './fileVisuals';
import {
  clearPePendingPreview,
  parsePeSearchState,
  peekPePendingPreview,
  stashPePendingPreview,
  type PeNavigateState,
  type PePendingPreview,
} from '../utils/searchState';

/**
 * Opens `FilePreviewDialog` when the user arrived via a universal-search
 * hit that stashed a pending preview (`?preview=<id>` + sessionStorage
 * and/or `location.state.pePendingPreview`).
 * Mount once on destination pages (EDM / Highlights).
 */
export default function SearchPendingPreviewHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const [pending, setPending] = useState<PePendingPreview | null>(null);

  useEffect(() => {
    const navState = (location.state as PeNavigateState | null) || null;
    const fromNav = navState?.pePendingPreview;
    if (fromNav?.id && fromNav.url && fromNav.title) {
      const normalized: PePendingPreview = {
        ...fromNav,
        id: String(fromNav.id).trim(),
      };
      stashPePendingPreview(normalized);
      setPending(normalized);
      return;
    }

    const { previewId } = parsePeSearchState(location.search);
    if (!previewId) {
      setPending(null);
      return;
    }
    // Peek (don't clear) so React Strict Mode's double-invoke can't
    // wipe the stash before the dialog mounts.
    const payload = peekPePendingPreview(previewId);
    if (payload) setPending(payload);
  }, [location.search, location.key, location.state]);

  const handleClose = () => {
    setPending(null);
    clearPePendingPreview();
    const params = new URLSearchParams(
      location.search.startsWith('?') ? location.search.slice(1) : location.search,
    );
    params.delete('preview');
    const qs = params.toString();
    // Always clear navigate state so a leftover `pePendingPreview`
    // cannot reopen the dialog after close.
    navigate(
      {
        pathname: location.pathname,
        search: qs ? `?${qs}` : '',
        hash: location.hash,
      },
      { replace: true, state: {} },
    );
  };

  if (!pending) return null;

  return (
    <FilePreviewDialog
      open
      onClose={handleClose}
      title={pending.title}
      url={pending.url}
      urlPublic={pending.urlPublic ?? null}
      filename={buildSafeDownloadFilename(pending.title, pending.filename)}
      forceBlobDownload={pending.forceBlobDownload ?? true}
    />
  );
}
